import { useState, useEffect } from 'react'
import { useAuth } from '../../auth/AuthContext'

interface Organization {
  id: string
  name: string
  role: string
  joinedAt: any
}

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

export function OrganizationSwitcher({ direction = 'down' }: { direction?: 'down' | 'up' } = {}) {
  const { user, firebaseUser } = useAuth()
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [switching, setSwitching] = useState(false)

  // @bp-g.jpユーザーのみ表示
  const isBlueprintUser = user?.email?.endsWith('@bp-g.jp')

  useEffect(() => {
    if (isBlueprintUser) {
      loadOrganizations()
    }
  }, [isBlueprintUser])

  const loadOrganizations = async () => {
    try {
      setLoading(true)

      if (!firebaseUser) return

      const token = await firebaseUser.getIdToken()
      const response = await fetch(`${API_BASE}/v1/user/organizations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) throw new Error('Failed to load organizations')

      const data = await response.json()
      setOrganizations(data.organizations || [])
    } catch (error) {
      console.error('Failed to load organizations:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSwitchOrganization = async (orgId: string) => {
    try {
      setSwitching(true)

      if (!firebaseUser) return

      const token = await firebaseUser.getIdToken()
      const response = await fetch(`${API_BASE}/v1/user/switch-organization`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ orgId })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to switch organization')
      }

      console.log('✅ Organization switched successfully')
      
      // トークンを再取得してページをリロード
      await firebaseUser.getIdToken(true)
      window.location.reload()

    } catch (error) {
      console.error('❌ Failed to switch organization:', error)
      alert(`組織の切り替えに失敗しました: ${error instanceof Error ? error.message : '不明なエラー'}`)
    } finally {
      setSwitching(false)
    }
  }

  // @bp-g.jpユーザー以外は表示しない
  if (!isBlueprintUser) {
    return null
  }

  // 現在の組織名を取得
  const currentOrg = organizations.find(org => org.id === user?.orgId)
  const currentOrgName = currentOrg?.name || '組織切り替え'

  return (
    <div className="relative" style={{ display: 'inline-block' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="btn btn-ghost btn-sm"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
        <span>{currentOrgName}</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ transform: isOpen ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s' }}>
          <path d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div
          className="fixed inset-0"
          style={{ zIndex: 10 }}
          onClick={() => setIsOpen(false)}
        />
      )}
      <div
        className={`dropdown-menu right${isOpen ? ' open' : ''}`}
        style={{
          width: 256,
          ...(direction === 'up' ? { top: 'auto', bottom: '100%', marginBottom: 4, left: 0, right: 'auto' } : {})
        }}
      >
        <div className="dropdown-menu-label">
          所属組織
        </div>

        {loading ? (
          <div className="px-3 py-4 text-center text-sm text-tertiary">
            読み込み中...
          </div>
        ) : organizations.length === 0 ? (
          <div className="px-3 py-4 text-center text-sm text-tertiary">
            所属組織がありません
          </div>
        ) : (
          <div>
            {organizations.map((org) => (
              <button
                key={org.id}
                onClick={() => handleSwitchOrganization(org.id)}
                disabled={switching || org.id === user?.orgId}
                className="dropdown-item"
                style={org.id === user?.orgId
                  ? { background: 'var(--color-accent-light)', color: 'var(--color-accent)' }
                  : switching ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              >
                <div className="flex items-center justify-between w-full">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{org.name}</div>
                    <div className="text-xs text-tertiary capitalize">{org.role}</div>
                  </div>
                  {org.id === user?.orgId && (
                    <svg className="flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
